import React, { memo, useCallback } from "react";
import { Col, Row } from "reactstrap";
import { StyledContainer } from "./index-styled";
import PT from "prop-types";
import Avatar from "./Avatar";
import Top from "./Top";
import Details from "./Details";
// import Progress from "./Progress/container";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/hooks/useTheme";
import Nav from "./Nav";
import ProfileContacts from "./Contacts";
import { Button } from "_c/button";
import { HashLink } from "@/components/HashLink";
import { useLocation } from "react-router-dom";
import { useProfileInfoPlaceholder } from "./hooks/useProfileInfoPlaceholder";
import CroppedText from "_c/CroppedText";
import Bio from "./Bio";

const Profile = (props) => {
  const { pathname, search } = useLocation();
  const theme = useTheme();
  const { t, ready } = useTranslation(
    [
      "common",
      "errors",
      props.pageType === "master" && "master_profile",
      props.pageType === "client" && "client_profile",
      props.pageType === "saloon" && "saloon_profile",
    ].filter(Boolean),
    {
      useSuspense: false,
    },
  );
  // const showProgress = props.pageType === "master" && props.isOwner;
  const toggleFavorite = useCallback(() => {
    props.toggleFavorite(props.favType, props.favId);
  }, [props.favType, props.favId]);
  const sendMessage = useCallback(
    (message) => {
      props.sendMessage(props.idForMessenger, message);
    },
    [props.idForMessenger],
  );
  let contactsComponent;
  if (props.pageType === "master" || props.pageType === "saloon") {
    contactsComponent = (
      <ProfileContacts
        sendMessage={sendMessage}
        isAuth={props.isAuth}
        isOwner={props.isOwner}
        phone={props.showPhone ? props.phone : null}
        vkId={props.vkId}
        fbId={props.fbId}
        instaId={props.instaId}
        tgId={props.tgId}
        waId={props.waId}
        viberId={props.viberId}
        t={t}
      />
    );
  }
  const bioPlaceholder = useProfileInfoPlaceholder({
    pageType: props.pageType,
    t,
    ready,
    isOwner: props.isOwner,
    enabled: Boolean(!props.bio && ready),
  });
  if (!ready) return null;
  return (
    <StyledContainer>
      <Row>
        <Col
          md={{ size: 4, offset: 0 }}
          sm={{ size: 4, offset: 0 }}
          xs={{ size: 5, offset: 0 }}
        >
          <Avatar
            t={t}
            avatar={props.avatar}
            altText={props.avatarAltText}
            sex={props.sex}
            pageType={props.pageType}
            isOwner={props.isOwner}
            updateAvatar={props.updateAvatar}
          />
        </Col>
        <Col
          md={{ size: 8, offset: 0 }}
          sm={{ size: 8, offset: 0 }}
          xs={{ size: 7, offset: 0 }}
        >
          <Top
            theme={theme}
            t={t}
            hl={props.hl}
            sendMessage={sendMessage}
            toggleFavorite={toggleFavorite}
            master={props.master}
            name={props.name}
            avatarAltText={props.avatarAltText}
            isOnline={props.isOnline}
            isFavorite={props.isFavorite}
            isOwner={props.isOwner}
            isAuth={props.isAuth}
            shareId={props.shareId}
            pageType={props.pageType}
            openShareModal={props.openShareModal}
            openAuthModal={props.openAuthModal}
            openWriteModal={props.openWriteModal}
          />
          <Details
            theme={theme}
            t={t}
            isPremium={props.isPremium}
            hidePhoneUnderButton={props.hidePhoneUnderButton}
            pageType={props.pageType}
            isOwner={props.isOwner}
            bio={props.bio}
            bioPlaceholder={bioPlaceholder}
            rate={props.rate}
            reviewsCount={props.reviewsCount}
            address={props.address}
            showPhone={props.pageType !== "master" && props.showPhone}
            phone={props.phone}
            openFreeSlots={props.openFreeSlots}
            contactsComponent={contactsComponent}
          />
          {/*showProgress && <Progress t={t} />*/}
        </Col>
      </Row>
      <div
        css={`
          display: none;
          @media (max-width: ${({ theme }) => theme.mobileBp}) {
            display: block;
          }
        `}
      >
        <Bio value={props.bio} placeholder={bioPlaceholder} />
      </div>
      {(props.pageType === "master" || props.pageType === "saloon") && (
        <>
          <div
            css={`
              display: none;
              @media (max-width: ${({ theme }) => theme.mobileBp}) {
                display: block;
              }
            `}
          >
            {contactsComponent}
          </div>
          <Nav t={t} />
        </>
      )}
      {(props.pageType === "master" || props.pageType === "saloon") && (
        <div
          css={`
            text-align: center;
            margin-top: 20px;
          `}
        >
          <Button
            as={HashLink}
            to={pathname + search + "#master-services-container"}
            css={`
              display: inline-block;
              @media (max-width: ${({ theme }) => theme.mobileBp}) {
                mawrgin: 0;
                display: block;
                width: 100%;
              }
            `}
            large
            greenish
          >
            {t("common:profile_book_cta_btn")}
          </Button>
        </div>
      )}
    </StyledContainer>
  );
};
Profile.displayName = "Profile";
Profile.propTypes = {
  avatar: PT.string,
  bio: PT.string,
  sex: PT.bool,
  pageType: PT.oneOf(["client", "master", "saloon"]).isRequired,
  isFavorite: PT.bool,
  isOnline: PT.bool,
  isOwner: PT.bool,
  isPremium: PT.bool,
  master: PT.object,
  name: PT.string,
  rate: PT.number,
  reviewsCount: PT.number,
  isAuth: PT.bool,
  favId: PT.string,
  favType: PT.string,
  idForMessenger: PT.string,
  shareId: PT.string,
  hl: PT.string,
  showPhone: PT.bool,
  phone: PT.string,
  hidePhoneUnderButton: PT.bool,
  fbId: PT.string,
  instaId: PT.string,
  tgId: PT.string,
  waId: PT.string,
  viberId: PT.string,
  vkId: PT.string,
  avatarAltText: PT.string,

  openFreeSlots: PT.func,
  openShareModal: PT.func.isRequired,
  sendMessage: PT.func.isRequired,
  updateAvatar: PT.func.isRequired,
  openAuthModal: PT.func.isRequired,
  toggleFavorite: PT.func.isRequired,
};

export default memo(Profile);
